import { useMemo } from "react";
import {
    useMutation,
    useQueries,
    useQuery,
    useQueryClient,
    type UseMutationResult,
    type UseQueryResult,
} from "@tanstack/react-query";
import { useApiClient } from "@shell/apiClientContext";
import { FLOORPLAN_QUERY_KEY } from "../common/queryKeys";
import {
    bulkImportAssets,
    createAsset,
    deleteAsset,
    lookupAsset,
    restoreAsset,
    searchAssets,
    updateAsset,
} from "./api";
import type {
    AssetUpsertPayload,
    AssetView,
    BulkImportAssetsPayload,
    BulkImportAssetsResult,
} from "./types";

export const ASSETS_QUERY_KEY = ["admin", "assets"] as const;
export const ASSET_LOOKUP_KEY = ["admin", "assets", "lookup"] as const;

/**
 * Resolves a single asset id to its view. Used by detail/label
 * components — `<AssetName id={...} />` is the typical caller. Returns
 * `null` (not an error) when the id is empty or the API returns 404, so
 * callers can fall back to displaying the raw id without special-casing
 * rejection states.
 *
 * `staleTime: Infinity` matches the user/workspace lookup hooks: the
 * fields shown by `formatAssetDisplay` (assetTag, type, make, model)
 * essentially never change inside a session, and the alternative — a
 * refetch on every focus return — adds latency to a screen we expect to
 * feel instantaneous after the first hit.
 */
export function useAssetEntry(
    id: string | null | undefined,
): UseQueryResult<AssetView | null, Error> {
    const client = useApiClient();
    const trimmed = typeof id === "string" ? id.trim() : "";

    return useQuery({
        queryKey: [...ASSET_LOOKUP_KEY, trimmed],
        queryFn: () => lookupAsset(client, trimmed),
        enabled: trimmed !== "",
        staleTime: Infinity,
    });
}

export interface AssetMatchInput {
    readonly serialNumber: string | null;
    readonly assetTag: string | null;
}

export interface AssetMatchResult {
    readonly isLoading: boolean;
    readonly match: AssetView | null;
}

/**
 * Asks the backend whether an asset already exists with the given serial
 * number or asset tag. Used by the asset form's photo-fill match detector:
 * after the recognizer returns a suggestion containing either field, we
 * check the existing inventory before letting the user re-key a duplicate.
 *
 * The search is fired only when at least one input is non-empty. Inputs
 * that change keep the same query key for stability across renders;
 * `staleTime: 60_000` keeps the result hot during a typical "snap, look
 * at the strip, decide" interaction without making the user wait again
 * if they back out and retry the same photo.
 */
export function useAssetMatch(input: AssetMatchInput): AssetMatchResult {
    const client = useApiClient();
    const sn = (input.serialNumber ?? "").trim();
    const tag = (input.assetTag ?? "").trim();
    const enabled = sn !== "" || tag !== "";

    const query = useQuery({
        queryKey: [...ASSETS_QUERY_KEY, "match", sn, tag],
        queryFn: () =>
            searchAssets(client, {
                ...(sn !== "" ? { serialNumber: sn } : {}),
                ...(tag !== "" ? { assetTag: tag } : {}),
                includeDeleted: false,
            }),
        enabled,
        staleTime: 60_000,
    });

    return {
        isLoading: enabled && query.isPending,
        // Search can return multiple in pathological cases (e.g. tag
        // collision against a different SN). The first row is the only
        // one we care about for "do we already have this?"; the
        // uniqueness guard on save will surface anything else.
        match: enabled ? (query.data?.[0] ?? null) : null,
    };
}

export interface AssetEntriesResult {
    readonly isLoading: boolean;
    readonly map: ReadonlyMap<string, AssetView | null>;
}

/**
 * Resolves a list of asset ids to a `id → asset | null` map. Mirrors
 * `useUserDisplayNames`: deduplicate + sort the input so `useQueries`
 * sees stable keys across renders, share the cache with `useAssetEntry`
 * (both write under `ASSET_LOOKUP_KEY`), and surface `null` for both the
 * pending and the not-found states so callers can fall back to the raw id
 * without special-casing rejection.
 *
 * Used by the workspace-assets panel to batch-resolve every assignment's
 * asset reference into a friendly label without firing N independent
 * round-trips that bypass cache sharing.
 */
export function useAssetEntries(ids: readonly string[]): AssetEntriesResult {
    const client = useApiClient();

    const unique = useMemo(() => {
        const seen = new Set<string>();
        for (const raw of ids) {
            const trimmed = raw.trim();
            if (trimmed === "") continue;
            seen.add(trimmed);
        }
        return Array.from(seen).sort();
    }, [ids]);

    return useQueries({
        queries: unique.map((id) => ({
            queryKey: [...ASSET_LOOKUP_KEY, id],
            queryFn: () => lookupAsset(client, id),
            staleTime: Infinity,
        })),
        combine: (results) => {
            const map = new Map<string, AssetView | null>();
            for (let i = 0; i < unique.length; i += 1) {
                const id = unique[i];
                if (id === undefined) continue;
                map.set(id, results[i]?.data ?? null);
            }
            return {
                isLoading: results.some((r) => r.isPending),
                map,
            };
        },
    });
}

export interface SearchAssetsParams {
    readonly q: string;
    readonly sageOfficeId: string;
    readonly type: string;
    readonly includeDeleted: boolean;
}

export function useAssets(params: SearchAssetsParams): UseQueryResult<readonly AssetView[], Error> {
    const client = useApiClient();
    return useQuery({
        queryKey: [
            ...ASSETS_QUERY_KEY,
            params.q,
            params.sageOfficeId,
            params.type,
            params.includeDeleted,
        ],
        queryFn: () =>
            searchAssets(client, {
                q: params.q,
                sageOfficeId: params.sageOfficeId,
                type: params.type,
                includeDeleted: params.includeDeleted,
            }),
        staleTime: 30_000,
    });
}

export function useCreateAsset(): UseMutationResult<AssetView, Error, AssetUpsertPayload> {
    const client = useApiClient();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (payload) => createAsset(client, payload),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ASSETS_QUERY_KEY });
        },
    });
}

export function useUpdateAsset(): UseMutationResult<AssetView, Error, AssetUpsertPayload> {
    const client = useApiClient();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (payload) => updateAsset(client, payload),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ASSETS_QUERY_KEY });
        },
    });
}

export function useDeleteAsset(): UseMutationResult<void, Error, string> {
    const client = useApiClient();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id) => deleteAsset(client, id),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ASSETS_QUERY_KEY });
        },
    });
}

export function useRestoreAsset(): UseMutationResult<AssetView, Error, string> {
    const client = useApiClient();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id) => restoreAsset(client, id),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ASSETS_QUERY_KEY });
        },
    });
}

/**
 * Bulk-import mutation. Even partial-success responses (some rows
 * failed) trip `onSuccess` because the HTTP call itself succeeded —
 * the page is responsible for reading `data.failed` and surfacing
 * per-row outcomes. The cache invalidation runs unconditionally so the
 * assets list reflects newly-created rows immediately.
 */
export function useBulkImportAssets(): UseMutationResult<
    BulkImportAssetsResult,
    Error,
    BulkImportAssetsPayload
> {
    const client = useApiClient();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (payload) => bulkImportAssets(client, payload),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ASSETS_QUERY_KEY });
            // Literal matches `ASSET_ASSIGNMENTS_QUERY_KEY` — imported
            // from that module it would cycle (that file already
            // imports `ASSETS_QUERY_KEY` from here).
            void queryClient.invalidateQueries({ queryKey: ["admin", "asset-assignments"] });
            void queryClient.invalidateQueries({ queryKey: FLOORPLAN_QUERY_KEY });
        },
    });
}
